import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Receipt, LogOut, Key, ChevronDown,
  Wallet, Users, Truck, MoreHorizontal, BarChart3, Shield, FileWarning, FileCheck, FlaskConical, TrendingUp
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import MJLogo from './MJLogo';
import axios from 'axios';
import { toast } from 'sonner';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userMenuRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMoreMenu && !e.target.closest('[data-more-menu]')) {
        setShowMoreMenu(false);
      }
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMoreMenu, showUserMenu]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Define menu items based on user role
  const allMenuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['approver', 'super_approver'] },
    { path: '/expenses', icon: Receipt, label: 'Expenses', roles: ['submitter', 'approver', 'super_approver'] },
    { path: '/credits', icon: TrendingUp, label: 'Credits', roles: ['approver', 'super_approver'] },
    { path: '/silica-loads', icon: Truck, label: 'Silica Loads', roles: ['approver', 'super_approver'] },
    { path: '/pending-bills', icon: FileWarning, label: 'Pending Bills', roles: ['approver', 'super_approver'] },
    { path: '/auditor', icon: FileCheck, label: 'Auditor', roles: ['approver', 'super_approver'] },
    { path: '/employees', icon: Users, label: 'Employees', roles: ['approver', 'super_approver'] },
    { path: '/reports', icon: BarChart3, label: 'Reports', roles: ['approver', 'super_approver'] },
    { path: '/investment', icon: Wallet, label: 'Investment', roles: ['approver', 'super_approver'] },
    { path: '/admin', icon: Shield, label: 'Admin', roles: ['super_approver'] },
    { path: '/misc', icon: FlaskConical, label: 'Misc', roles: ['approver', 'super_approver'] },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => item.roles.includes(user.role));

  // Mobile: Show first 3 items in bottom nav
  const mobileNavItems = menuItems.slice(0, Math.min(3, menuItems.length));
  // Everything else goes in "More" menu
  const mobileMoreItems = menuItems.slice(Math.min(3, menuItems.length));

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      
      {/* Gradient Background - Fixed behind everything */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 z-0 pointer-events-none" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100/30 via-transparent to-transparent z-0 pointer-events-none" />
      
      {/* Desktop Glassmorphism Navigation - Hidden on mobile */}
      <header
        data-testid="desktop-sidebar"
        className="fixed top-4 left-4 right-4 h-14 z-50 hidden lg:block"
      >
        <nav className="h-full px-4 bg-white/15 backdrop-blur-xl border border-white/20 rounded-2xl flex items-center justify-between shadow-lg shadow-black/5">
          {/* Logo */}
          <div className="flex items-center gap-6">
              <div className="flex items-center gap-2.5 cursor-pointer group"
              onClick={() => navigate('/dashboard')}
            >
              <MJLogo size={36} />
              <div className="hidden xl:block">
                <h1 className="text-slate-800 font-semibold text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  M&J Enterprises
                </h1>
                <p className="text-slate-700 text-[10px] font-medium">We do it anyway!</p>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-0.5">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    onClick={() => navigate(item.path)}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 text-sm font-medium btn-press
                      ${isActive
                        ? 'bg-white/30 text-slate-900 shadow-inner border border-white/40'
                        : 'text-slate-700 hover:bg-white/20 hover:text-slate-900 hover:scale-105'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 transition-transform duration-200 ${!isActive && 'group-hover:scale-110'}`} strokeWidth={1.5} />
                    <span className="hidden 2xl:inline">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* User Section */}
          <div className="relative" ref={userMenuRef}>
            <button
              data-testid="user-menu"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-white/20 transition-all group"
            >
              <div className="w-8 h-8 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 group-hover:bg-white/40 transition-all">
                <span className="text-slate-800 text-sm font-semibold">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="text-left hidden xl:block">
                <p className="text-slate-800 text-sm font-medium">{user.name || 'User'}</p>
                <p className="text-slate-600 text-xs">{user.role?.replace('_', ' ')}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden dropdown-animate">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-slate-800 font-medium text-sm">{user.name || 'User'}</p>
                  <p className="text-slate-500 text-xs">{user.email}</p>
                </div>
                <div className="p-1.5">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowChangePassword(true);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-700 hover:bg-slate-100 rounded-xl transition-all duration-200 text-sm btn-press"
                    data-testid="change-password-btn"
                  >
                    <Key className="w-4 h-4" />
                    Change Password
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 text-sm btn-press"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Bottom Navigation Only - Fixed at very bottom */}
      <nav 
        className="fixed bottom-0 left-0 right-0 z-[100] lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', touchAction: 'manipulation', pointerEvents: 'auto' }}
        data-testid="mobile-bottom-nav"
      >
        <div className="h-16 px-2 flex items-center justify-around" style={{ touchAction: 'manipulation', pointerEvents: 'auto' }}>
          {/* MJ Logo */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center gap-0.5 px-2 py-2"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-slate-600 to-slate-800 rounded-xl flex items-center justify-center shadow-md">
              <MJLogo size={36} />
            </div>
          </button>

          {/* Main Nav Items: Dashboard, Expenses, Silica Loads */}
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`
                  flex flex-col items-center justify-center gap-0.5 flex-1 py-2 rounded-xl transition-all
                  ${isActive 
                    ? 'text-slate-900' 
                    : 'text-slate-600'
                  }
                `}
              >
                <div className={`p-1.5 rounded-lg transition-all ${isActive ? 'bg-white/30' : ''}`}>
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}
          
          {/* More Menu */}
          <div className="relative flex-1" data-more-menu>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreMenu(!showMoreMenu);
              }}
              data-testid="mobile-more-menu"
              className={`
                flex flex-col items-center justify-center gap-0.5 w-full py-2 rounded-xl transition-all
                ${showMoreMenu ? 'text-slate-900' : 'text-slate-600'}
              `}
            >
              <div className={`p-1.5 rounded-lg transition-all ${showMoreMenu ? 'bg-white/30' : ''}`}>
                <MoreHorizontal className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-medium">More</span>
            </button>
            
            {/* More Menu Popup */}
            {showMoreMenu && (
              <div className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden dropdown-animate" style={{ transformOrigin: 'bottom right' }}>
                <div className="p-2">
                  {mobileMoreItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setShowMoreMenu(false);
                        }}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 btn-press
                          ${isActive 
                            ? 'bg-slate-100 text-slate-700' 
                            : 'text-slate-700 hover:bg-slate-50 hover:translate-x-1'
                          }
                        `}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="border-t border-slate-200 p-2">
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      setShowChangePassword(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-xl transition-all duration-200 btn-press hover:translate-x-1"
                    data-testid="mobile-change-password-btn"
                  >
                    <Key className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Change Password</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 btn-press hover:translate-x-1"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main 
        className="relative z-10 pt-6 pb-32 px-4 lg:pt-24 lg:pb-8 lg:px-6 min-h-screen"
      >
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl shadow-black/5 p-6 lg:p-8 min-h-[calc(100vh-8rem)] mb-4 lg:mb-0 animate-fade-in-up">
            {children}
          </div>
        </div>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="sm:max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Change Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Current Password</Label>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter current password"
                required
                className="h-11 rounded-xl border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">New Password</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password"
                required
                className="h-11 rounded-xl border-slate-200"
              />
              <PasswordStrengthIndicator password={passwordData.newPassword} />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                required
                className="h-11 rounded-xl border-slate-200"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowChangePassword(false)}
                className="flex-1 h-11 rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading}
                className="flex-1 h-11 bg-gradient-to-r from-slate-600 to-slate-800 hover:from-slate-700 hover:to-slate-900 text-white rounded-xl border-0"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
